import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  LayoutDashboard, Image, Package, Newspaper, Mail, Users, Settings, LogOut,
  Plus, Trash2, Edit2, Check, X, Eye, FileText, Calendar, PlayCircle, Send,
  Upload, Link, Building, Key, Shield, BarChart3, Wrench
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { getImageUrl } from "../lib/utils";
import { ClientManager, GeneratorManager } from "./ClientLicenseManager";
import NewsletterManager from "./NewsletterManager";
import UserManager from "./UserManager";
import AnalyticsDashboard from "./AnalyticsDashboard";
import LicenseIntegrationGuide from "./LicenseIntegrationGuide";

// Reusable Image Upload component
const ImageUploader = ({ value, onChange, headers, label = "Image", required = false }) => {
  const [mode, setMode] = useState(value && !value.startsWith("/api/uploads/") ? "url" : "upload");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("Fichier trop volumineux (max 5 Mo)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, formData, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const url = res.data.url;
      setPreview(url);
      onChange(url);
    } catch (err) {
      alert("Erreur lors de l'upload: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-400">{label}</label>
        <div className="flex gap-1 ml-auto">
          <button type="button" onClick={() => setMode("upload")} className={`px-3 py-1 text-xs rounded-l-lg border border-white/10 ${mode === "upload" ? "bg-[#2395E3]/30 text-[#2395E3]" : "text-slate-400"}`}>
            <Upload className="w-3 h-3 inline mr-1" />Upload
          </button>
          <button type="button" onClick={() => setMode("url")} className={`px-3 py-1 text-xs rounded-r-lg border border-white/10 ${mode === "url" ? "bg-[#2395E3]/30 text-[#2395E3]" : "text-slate-400"}`}>
            <Link className="w-3 h-3 inline mr-1" />URL
          </button>
        </div>
      </div>
      {mode === "upload" ? (
        <div className="flex items-center gap-3">
          <input type="file" ref={fileRef} accept="image/*" onChange={handleFileUpload} className="hidden" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex-1 px-4 py-3 rounded-lg bg-[#0B1121] border border-dashed border-white/20 hover:border-[#2395E3] text-sm text-slate-400 transition-colors text-center">
            {uploading ? "Upload en cours..." : (value ? "Changer l'image" : "Cliquez pour uploader (max 5 Mo)")}
          </button>
        </div>
      ) : (
        <input type="url" placeholder="https://exemple.com/image.jpg" value={value && !value.startsWith("/api/uploads/") ? value : ""} 
          onChange={(e) => { onChange(e.target.value); setPreview(e.target.value); }} 
          className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" required={required && !value} />
      )}
      {(preview || value) && (
        <div className="flex items-center gap-3">
          <img src={preview?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${preview}` : (preview || value)} alt="Aperçu" className="h-16 w-24 object-cover rounded-lg border border-white/10" onError={(e) => e.target.style.display = "none"} />
          {value && <span className="text-xs text-slate-500 truncate flex-1">{value}</span>}
        </div>
      )}
    </div>
  );
};

const AdminPanel = ({ onClose, credentials, onLogout }) => {
  const [activeTab, setActiveTab] = useState("analytics");
  const [data, setData] = useState({
    carousel: [], software: [], news: [], newsletter: [], contacts: [], demoRequests: [], settings: {}, legal: {}, emailSettings: {}
  });
  const [loading, setLoading] = useState(true);

  // Headers for API calls — Basic Auth or cookie-based session
  const headers = credentials
    ? { Authorization: `Basic ${btoa(`${credentials.username}:${credentials.password}`)}` }
    : {};
  
  const axiosConfig = credentials ? { headers } : { headers, withCredentials: true };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [carousel, software, news, newsletter, contacts, demoRequests, settings, legal, emailSettings] = await Promise.all([
        axios.get(`${API}/admin/carousel`, axiosConfig),
        axios.get(`${API}/admin/software`, axiosConfig),
        axios.get(`${API}/admin/news`, axiosConfig),
        axios.get(`${API}/admin/newsletter`, axiosConfig),
        axios.get(`${API}/admin/contacts`, axiosConfig),
        axios.get(`${API}/admin/demo-requests`, axiosConfig),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/legal`, axiosConfig),
        axios.get(`${API}/admin/email-settings`, axiosConfig),
      ]);
      setData({
        carousel: carousel.data,
        software: software.data,
        news: news.data,
        newsletter: newsletter.data,
        contacts: contacts.data,
        demoRequests: demoRequests.data,
        settings: settings.data,
        legal: legal.data,
        emailSettings: emailSettings.data,
      });
    } catch (err) {
      console.error("Fetch error:", err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const seedData = async () => {
    try {
      await axios.post(`${API}/admin/seed`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Supprimer cet élément ?")) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const markDemoRead = async (id) => {
    try {
      await axios.put(`${API}/admin/demo-requests/${id}/read`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/read`, {}, { headers });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const tabs = [
    { id: "analytics", icon: BarChart3, label: "Analytics" },
    { id: "demoRequests", icon: PlayCircle, label: "Demandes demo" },
    { id: "carousel", icon: Image, label: "Carrousel" },
    { id: "software", icon: Package, label: "Logiciels" },
    { id: "news", icon: Newspaper, label: "Actualites" },
    { id: "clients", icon: Building, label: "Clients" },
    { id: "generators", icon: Key, label: "Generateurs" },
    { id: "newsletter", icon: Mail, label: "Newsletter" },
    { id: "contacts", icon: Users, label: "Messages" },
    { id: "users", icon: Shield, label: "Utilisateurs" },
    { id: "settings", icon: Settings, label: "Coordonnees" },
    { id: "legal", icon: FileText, label: "Mentions legales" },
    { id: "email", icon: Send, label: "Notifications" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#0B1121]" data-testid="admin-panel">
      <div className="flex h-full">
        <aside className="w-64 bg-[#131B2F] border-r border-white/10 p-6 flex flex-col overflow-y-auto">
          <div className="flex items-center gap-3 mb-8">
            <LayoutDashboard className="w-8 h-8 text-[#00CCC2]" />
            <div>
              <h2 className="font-bold">Admin SCIAD</h2>
              <p className="text-xs text-slate-400">{credentials ? credentials.username : ""}</p>
            </div>
          </div>
          
          <nav className="flex-1 space-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? "bg-[#2395E3]/20 text-[#00CCC2]"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
                {tab.id === "contacts" && data.contacts.filter(c => !c.read).length > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-red-500">
                    {data.contacts.filter(c => !c.read).length}
                  </span>
                )}
                {tab.id === "demoRequests" && data.demoRequests.filter(d => !d.read).length > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs rounded-full bg-[#00CCC2]">
                    {data.demoRequests.filter(d => !d.read).length}
                  </span>
                )}
              </button>
            ))}
          </nav>
          
          <div className="space-y-2 pt-4 border-t border-white/10">
            <button onClick={seedData} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all" data-testid="seed-data-btn">
              <Settings className="w-5 h-5" />
              Initialiser données
            </button>
            <button onClick={onLogout || onClose} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all" data-testid="admin-logout">
              <LogOut className="w-5 h-5" />
              Retour au site
            </button>
          </div>
        </aside>

        <main className="flex-1 p-8 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin w-8 h-8 border-2 border-[#00CCC2] border-t-transparent rounded-full" />
            </div>
          ) : (
            <>
              {activeTab === "analytics" && <AnalyticsDashboard headers={headers} />}
              {activeTab === "demoRequests" && <DemoRequestsManager items={data.demoRequests} onDelete={(id) => deleteItem("demo-requests", id)} onMarkRead={markDemoRead} />}
              {activeTab === "carousel" && <CarouselManager items={data.carousel} headers={headers} onRefresh={fetchData} onDelete={(id) => deleteItem("carousel", id)} />}
              {activeTab === "software" && <SoftwareManager items={data.software} headers={headers} onRefresh={fetchData} onDelete={(id) => deleteItem("software", id)} />}
              {activeTab === "news" && <NewsManager items={data.news} headers={headers} onRefresh={fetchData} onDelete={(id) => deleteItem("news", id)} />}
              {activeTab === "clients" && <ClientManager headers={headers} />}
              {activeTab === "generators" && (
                <>
                  <GeneratorManager headers={headers} />
                  <div className="mt-8">
                    <h2 className="text-xl font-bold mb-4">Code d'integration</h2>
                    <p className="text-slate-400 text-sm mb-4">Copiez le code ci-dessous dans vos applications pour verifier les licences generees par SCIAD.</p>
                    <LicenseIntegrationGuide />
                  </div>
                </>
              )}
              {activeTab === "newsletter" && <NewsletterManager headers={headers} />}
              {activeTab === "contacts" && <ContactsManager items={data.contacts} onDelete={(id) => deleteItem("contacts", id)} onMarkRead={markRead} />}
              {activeTab === "users" && <UserManager headers={headers} />}
              {activeTab === "settings" && <SettingsManager settings={data.settings} headers={headers} onRefresh={fetchData} />}
              {activeTab === "legal" && <LegalManager legal={data.legal} headers={headers} onRefresh={fetchData} />}
              {activeTab === "email" && <EmailSettingsManager settings={data.emailSettings} headers={headers} onRefresh={fetchData} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

// Carousel Manager with dates
const CarouselManager = ({ items, headers, onRefresh, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: "", subtitle: "", description: "", image_url: "", cta_text: "Découvrir", cta_link: "#solutions", order: 0, active: true, start_date: "", end_date: ""
  });

  const resetForm = () => {
    setForm({ title: "", subtitle: "", description: "", image_url: "", cta_text: "Découvrir", cta_link: "#solutions", order: 0, active: true, start_date: "", end_date: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title, subtitle: item.subtitle, description: item.description, image_url: item.image_url,
      cta_text: item.cta_text, cta_link: item.cta_link, order: item.order, active: item.active,
      start_date: item.start_date ? item.start_date.split('T')[0] : "",
      end_date: item.end_date ? item.end_date.split('T')[0] : ""
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/carousel/${editingId}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/carousel`, payload, { headers });
      }
      resetForm();
      onRefresh();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Gestion du Carrousel</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg" data-testid="add-carousel-btn">
          <Plus className="w-5 h-5" /> Ajouter
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Titre" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            <input type="text" placeholder="Sous-titre" required value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <textarea placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          <ImageUploader value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} headers={headers} label="Image du carrousel" required />
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Texte du bouton" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            <input type="text" placeholder="Lien du bouton" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2"><Calendar className="w-4 h-4 inline mr-1" />Date de début (optionnel)</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2"><Calendar className="w-4 h-4 inline mr-1" />Date de fin (optionnel)</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-5 h-5 rounded" />
              <span>Actif</span>
            </label>
            <input type="number" placeholder="Ordre" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-24 px-4 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg">{editingId ? 'Modifier' : 'Enregistrer'}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {items.map((slide) => (
          <div key={slide.id} className="flex items-center gap-4 p-4 rounded-xl bg-[#131B2F] border border-white/10">
            <img src={getImageUrl(slide.image_url)} alt={slide.title} className="w-24 h-16 object-cover rounded-lg" />
            <div className="flex-1">
              <h3 className="font-semibold">{slide.title}</h3>
              <p className="text-sm text-slate-400">{slide.subtitle}</p>
              {(slide.start_date || slide.end_date) && (
                <p className="text-xs text-[#00CCC2] mt-1">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {slide.start_date ? new Date(slide.start_date).toLocaleDateString('fr-FR') : '...'} - {slide.end_date ? new Date(slide.end_date).toLocaleDateString('fr-FR') : '...'}
                </p>
              )}
            </div>
            <span className={`px-3 py-1 text-xs rounded-full ${slide.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {slide.active ? 'Actif' : 'Inactif'}
            </span>
            <button onClick={() => handleEdit(slide)} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Edit2 className="w-5 h-5" /></button>
            <button onClick={() => onDelete(slide.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Software Manager (simplified for space)
const SoftwareManager = ({ items, headers, onRefresh, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ slug: "", name: "", tagline: "", description: "", hero_image_url: "", icon: "Monitor", features: [], technologies: [], sections: [], cta_text: "Demander une démo", cta_link: "#contact", order: 0, active: true });
  const [featureInput, setFeatureInput] = useState("");
  const [techInput, setTechInput] = useState("");

  const resetForm = () => {
    setForm({ slug: "", name: "", tagline: "", description: "", hero_image_url: "", icon: "Monitor", features: [], technologies: [], sections: [], cta_text: "Demander une démo", cta_link: "#contact", order: 0, active: true });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({ ...item, sections: item.sections || [] });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/software/${editingId}`, form, { headers });
      } else {
        await axios.post(`${API}/admin/software`, form, { headers });
      }
      resetForm();
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const addSection = () => {
    setForm({ ...form, sections: [...form.sections, { title: "", description: "", image_url: "", features: [] }] });
  };

  const updateSection = (index, field, value) => {
    const newSections = [...form.sections];
    newSections[index][field] = value;
    setForm({ ...form, sections: newSections });
  };

  const removeSection = (index) => {
    setForm({ ...form, sections: form.sections.filter((_, i) => i !== index) });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Gestion des Logiciels</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg">
          <Plus className="w-5 h-5" /> Ajouter
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <input type="text" placeholder="Slug (url)" required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            <input type="text" placeholder="Nom du logiciel" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          </div>
          <input type="text" placeholder="Tagline (accroche)" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          <textarea placeholder="Description" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          <ImageUploader value={form.hero_image_url || ""} onChange={(url) => setForm({ ...form, hero_image_url: url })} headers={headers} label="Image hero (optionnel)" />
          
          <div>
            <label className="text-sm text-slate-400 mb-2 block">Fonctionnalités principales</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} className="flex-1 px-4 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none" placeholder="Ajouter" />
              <button type="button" onClick={() => { if (featureInput.trim()) { setForm({ ...form, features: [...form.features, featureInput.trim()] }); setFeatureInput(""); } }} className="px-4 py-2 bg-[#2395E3]/20 rounded-lg"><Plus className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.features.map((f, i) => (
                <span key={i} className="px-3 py-1 bg-[#0B1121] rounded-full text-sm flex items-center gap-2">{f}<button type="button" onClick={() => setForm({ ...form, features: form.features.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></button></span>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm text-slate-400 mb-2 block">Technologies</label>
            <div className="flex gap-2 mb-2">
              <input type="text" value={techInput} onChange={(e) => setTechInput(e.target.value)} className="flex-1 px-4 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none" placeholder="Ajouter" />
              <button type="button" onClick={() => { if (techInput.trim()) { setForm({ ...form, technologies: [...form.technologies, techInput.trim()] }); setTechInput(""); } }} className="px-4 py-2 bg-[#2395E3]/20 rounded-lg"><Plus className="w-5 h-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.technologies.map((t, i) => (
                <span key={i} className="px-3 py-1 bg-[#2395E3]/20 text-[#2395E3] rounded-full text-sm flex items-center gap-2">{t}<button type="button" onClick={() => setForm({ ...form, technologies: form.technologies.filter((_, idx) => idx !== i) })}><X className="w-4 h-4" /></button></span>
              ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="flex justify-between items-center mb-4">
              <label className="text-sm text-slate-400">Sections de la page détail</label>
              <button type="button" onClick={addSection} className="text-sm text-[#00CCC2] hover:underline">+ Ajouter une section</button>
            </div>
            {form.sections.map((section, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-[#0B1121] mb-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">Section {idx + 1}</span>
                  <button type="button" onClick={() => removeSection(idx)} className="text-red-400 text-sm">Supprimer</button>
                </div>
                <input type="text" placeholder="Titre de la section" value={section.title} onChange={(e) => updateSection(idx, 'title', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" />
                <textarea placeholder="Description" value={section.description} onChange={(e) => updateSection(idx, 'description', e.target.value)} className="w-full px-3 py-2 rounded-lg bg-[#131B2F] border border-white/10 outline-none text-sm" rows={2} />
                <ImageUploader value={section.image_url || ""} onChange={(url) => updateSection(idx, 'image_url', url)} headers={headers} label="Image de section (optionnel)" />
              </div>
            ))}
          </div>

          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg">{editingId ? 'Modifier' : 'Enregistrer'}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {items.map((sw) => (
          <div key={sw.id} className="p-4 rounded-xl bg-[#131B2F] border border-white/10">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="font-semibold text-lg">{sw.name}</h3>
                <p className="text-sm text-[#00CCC2]">/logiciel/{sw.slug}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(sw)} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Edit2 className="w-5 h-5" /></button>
                <button onClick={() => onDelete(sw.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-3">{sw.description}</p>
            <div className="flex flex-wrap gap-2">
              {sw.technologies?.map((t, i) => (<span key={i} className="px-2 py-1 text-xs bg-[#2395E3]/20 text-[#2395E3] rounded-full">{t}</span>))}
            </div>
            <p className="text-xs text-slate-500 mt-2">{sw.sections?.length || 0} section(s) configurée(s)</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// News Manager with dates
const NewsManager = ({ items, headers, onRefresh, onDelete }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", image_url: "", author: "SCIAD", published: true, start_date: "", end_date: "" });

  const resetForm = () => {
    setForm({ title: "", excerpt: "", content: "", image_url: "", author: "SCIAD", published: true, start_date: "", end_date: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title, excerpt: item.excerpt, content: item.content, image_url: item.image_url || "",
      author: item.author, published: item.published,
      start_date: item.start_date ? item.start_date.split('T')[0] : "",
      end_date: item.end_date ? item.end_date.split('T')[0] : ""
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/news/${editingId}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/news`, payload, { headers });
      }
      resetForm();
      onRefresh();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Gestion des Actualités</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-2 px-4 py-2 btn-primary rounded-lg">
          <Plus className="w-5 h-5" /> Ajouter
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-8 p-6 rounded-xl bg-[#131B2F] border border-white/10 space-y-4">
          <input type="text" placeholder="Titre" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
          <textarea placeholder="Résumé" required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" rows={2} />
          <textarea placeholder="Contenu complet" required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" rows={5} />
          <ImageUploader value={form.image_url || ""} onChange={(url) => setForm({ ...form, image_url: url })} headers={headers} label="Image de l'article (optionnel)" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2"><Calendar className="w-4 h-4 inline mr-1" />Date de début (optionnel)</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2"><Calendar className="w-4 h-4 inline mr-1" />Date de fin (optionnel)</label>
              <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#0B1121] border border-white/10 outline-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="w-5 h-5 rounded" />
            <span>Publié</span>
          </label>
          <div className="flex gap-4">
            <button type="submit" className="px-6 py-2 btn-primary rounded-lg">{editingId ? 'Modifier' : 'Enregistrer'}</button>
            <button type="button" onClick={resetForm} className="px-6 py-2 rounded-lg border border-white/20">Annuler</button>
          </div>
        </form>
      )}

      <div className="grid gap-4">
        {items.map((article) => (
          <div key={article.id} className="flex items-center gap-4 p-4 rounded-xl bg-[#131B2F] border border-white/10">
            {article.image_url && <img src={getImageUrl(article.image_url)} alt={article.title} className="w-24 h-16 object-cover rounded-lg" />}
            <div className="flex-1">
              <h3 className="font-semibold">{article.title}</h3>
              <p className="text-sm text-slate-400 line-clamp-1">{article.excerpt}</p>
              {(article.start_date || article.end_date) && (
                <p className="text-xs text-[#00CCC2] mt-1">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {article.start_date ? new Date(article.start_date).toLocaleDateString('fr-FR') : '...'} - {article.end_date ? new Date(article.end_date).toLocaleDateString('fr-FR') : '...'}
                </p>
              )}
            </div>
            <span className={`px-3 py-1 text-xs rounded-full ${article.published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              {article.published ? 'Publié' : 'Brouillon'}
            </span>
            <button onClick={() => handleEdit(article)} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Edit2 className="w-5 h-5" /></button>
            <button onClick={() => onDelete(article.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Newsletter Manager
// Contacts Manager
const ContactsManager = ({ items, onDelete, onMarkRead }) => {
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Messages de Contact</h1>
        <span className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400">{items.filter(c => !c.read).length} non lus</span>
      </div>
      <div className="space-y-4">
        {items.map((msg) => (
          <div key={msg.id} className={`rounded-xl border transition-all ${msg.read ? 'bg-[#131B2F] border-white/10' : 'bg-[#2395E3]/10 border-[#2395E3]/30'}`}>
            <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(expanded === msg.id ? null : msg.id)}>
              <div className={`w-3 h-3 rounded-full ${msg.read ? 'bg-slate-500' : 'bg-[#00CCC2]'}`} />
              <div className="flex-1">
                <h3 className="font-semibold">{msg.subject}</h3>
                <p className="text-sm text-slate-400">{msg.name} - {msg.email}</p>
              </div>
              <span className="text-sm text-slate-400">{new Date(msg.created_at).toLocaleDateString('fr-FR')}</span>
              {!msg.read && <button onClick={(e) => { e.stopPropagation(); onMarkRead(msg.id); }} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Eye className="w-5 h-5" /></button>}
              <button onClick={(e) => { e.stopPropagation(); onDelete(msg.id); }} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
            </div>
            {expanded === msg.id && (
              <div className="px-4 pb-4"><div className="p-4 rounded-lg bg-[#0B1121]"><p className="whitespace-pre-wrap">{msg.message}</p></div></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Settings Manager
const SettingsManager = ({ settings, headers, onRefresh }) => {
  const [form, setForm] = useState(settings);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setForm(settings); }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/settings`, form, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onRefresh();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-8">Coordonnées de l'entreprise</h1>
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Nom de l'entreprise</label>
            <input type="text" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Slogan</label>
            <input type="text" value={form.slogan || ""} onChange={(e) => setForm({ ...form, slogan: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-2">Adresse</label>
          <input type="text" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">Email</label>
            <input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Téléphone</label>
            <input type="text" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-2">LinkedIn (URL)</label>
            <input type="url" value={form.linkedin || ""} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-2">Twitter (URL)</label>
            <input type="url" value={form.twitter || ""} onChange={(e) => setForm({ ...form, twitter: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
          </div>
        </div>
        <div className="border-t border-white/10 pt-6">
          <h3 className="font-semibold mb-4">Informations légales</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">SIRET</label>
              <input type="text" value={form.siret || ""} onChange={(e) => setForm({ ...form, siret: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Capital</label>
              <input type="text" value={form.capital || ""} onChange={(e) => setForm({ ...form, capital: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">RCS</label>
              <input type="text" value={form.rcs || ""} onChange={(e) => setForm({ ...form, rcs: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" />
            </div>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#2395E3]" /> Google Analytics</h3>
          <div>
            <label className="block text-sm text-slate-400 mb-2">ID de mesure Google Analytics (ex: G-XXXXXXXXXX)</label>
            <input type="text" value={form.ga_id || ""} onChange={(e) => setForm({ ...form, ga_id: e.target.value })} placeholder="G-XXXXXXXXXX"
              className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none" data-testid="ga-id-input" />
            <p className="text-xs text-slate-500 mt-1">Le tracking ne sera activé qu'après acceptation des cookies par le visiteur (conformité RGPD).</p>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2"><Wrench className="w-5 h-5 text-amber-400" /> Mode Maintenance</h3>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.maintenance_mode || false} onChange={(e) => setForm({ ...form, maintenance_mode: e.target.checked })}
                className="w-5 h-5 rounded accent-[#00CCC2]" data-testid="maintenance-toggle" />
              <span className="text-sm">Activer le mode maintenance</span>
            </label>
            {form.maintenance_mode && <span className="px-3 py-1 text-xs rounded-full bg-amber-500/20 text-amber-400">Le site affichera une page de maintenance aux visiteurs</span>}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button type="submit" className="px-6 py-3 btn-primary rounded-lg">Enregistrer</button>
          {saved && <span className="text-green-400 flex items-center gap-2"><Check className="w-5 h-5" /> Enregistré !</span>}
        </div>
      </form>
    </div>
  );
};

// Legal Manager
const LegalManager = ({ legal, headers, onRefresh }) => {
  const [form, setForm] = useState(legal);
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState("mentions_legales");

  useEffect(() => { setForm(legal); }, [legal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/legal`, form, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const tabs = [
    { id: "mentions_legales", label: "Mentions légales" },
    { id: "politique_confidentialite", label: "Politique de confidentialité" },
    { id: "cgv", label: "CGV" },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-8">Pages légales</h1>
      <div className="flex gap-2 mb-6">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-lg transition-all ${activeTab === tab.id ? 'bg-[#2395E3]/20 text-[#00CCC2]' : 'text-slate-400 hover:text-white'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm text-slate-400 mb-2">Contenu HTML</label>
          <textarea
            value={form[activeTab] || ""}
            onChange={(e) => setForm({ ...form, [activeTab]: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none font-mono text-sm"
            rows={15}
            placeholder="<h2>Titre</h2>\n<p>Contenu...</p>"
          />
        </div>
        <p className="text-sm text-slate-500">Utilisez du HTML pour formater le texte. Ex: &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;a href=&quot;...&quot;&gt;</p>
        <div className="flex items-center gap-4">
          <button type="submit" className="px-6 py-3 btn-primary rounded-lg">Enregistrer</button>
          {saved && <span className="text-green-400 flex items-center gap-2"><Check className="w-5 h-5" /> Enregistré !</span>}
        </div>
      </form>
    </div>
  );
};

// Demo Requests Manager
const DemoRequestsManager = ({ items, onDelete, onMarkRead }) => {
  const [expanded, setExpanded] = useState(null);
  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Demandes de démonstration</h1>
        <span className="px-4 py-2 rounded-lg bg-[#00CCC2]/20 text-[#00CCC2]">{items.filter(d => !d.read).length} nouvelles</span>
      </div>
      <div className="space-y-4">
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-400">Aucune demande de démo pour le moment</div>
        ) : items.map((demo) => (
          <div key={demo.id} className={`rounded-xl border transition-all ${demo.read ? 'bg-[#131B2F] border-white/10' : 'bg-[#00CCC2]/10 border-[#00CCC2]/30'}`}>
            <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpanded(expanded === demo.id ? null : demo.id)}>
              <div className={`w-3 h-3 rounded-full ${demo.read ? 'bg-slate-500' : 'bg-[#00CCC2]'}`} />
              <div className="flex-1">
                <h3 className="font-semibold">{demo.company}</h3>
                <p className="text-sm text-slate-400">{demo.name} - {demo.email}</p>
              </div>
              <span className="px-3 py-1 text-xs rounded-full bg-[#2395E3]/20 text-[#2395E3]">{demo.software_interest}</span>
              <span className="text-sm text-slate-400">{new Date(demo.created_at).toLocaleDateString('fr-FR')}</span>
              {!demo.read && <button onClick={(e) => { e.stopPropagation(); onMarkRead(demo.id); }} className="p-2 text-[#00CCC2] hover:bg-[#00CCC2]/20 rounded-lg"><Eye className="w-5 h-5" /></button>}
              <button onClick={(e) => { e.stopPropagation(); onDelete(demo.id); }} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
            </div>
            {expanded === demo.id && (
              <div className="px-4 pb-4">
                <div className="p-4 rounded-lg bg-[#0B1121] space-y-2">
                  <p><strong>Téléphone:</strong> {demo.phone}</p>
                  <p><strong>Entreprise:</strong> {demo.company}</p>
                  <p><strong>Logiciel:</strong> {demo.software_interest}</p>
                  {demo.message && <p><strong>Message:</strong><br/>{demo.message}</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Email Settings Manager
const EmailSettingsManager = ({ settings, headers, onRefresh }) => {
  const [form, setForm] = useState(settings || {});
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => { setForm(settings || {}); }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-settings`, form, { headers });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const testEmail = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/admin/test-email`, {}, { headers });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ status: "error", message: "Erreur lors du test" });
    }
    setTesting(false);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-8">Notifications Email</h1>
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <div className="p-4 rounded-xl bg-[#131B2F]/50 border border-white/10">
          <p className="text-sm text-slate-400 mb-4">
            Configurez l'envoi d'emails de notification pour recevoir les demandes de démo et messages de contact directement dans votre boîte mail.
          </p>
        </div>

        <div>
          <label className="block text-sm text-slate-400 mb-2">Fournisseur d'email</label>
          <select
            value={form.provider || "none"}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none"
          >
            <option value="none">Désactivé (stockage local uniquement)</option>
            <option value="resend">Resend</option>
            <option value="sendgrid">SendGrid</option>
          </select>
        </div>

        {form.provider && form.provider !== "none" && (
          <>
            <div>
              <label className="block text-sm text-slate-400 mb-2">Clé API {form.provider}</label>
              <input
                type="password"
                value={form.api_key || ""}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="Entrez votre clé API"
                className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none"
              />
              <p className="text-xs text-slate-500 mt-1">
                {form.provider === "resend" ? "Obtenez votre clé sur resend.com/api-keys" : "Obtenez votre clé sur app.sendgrid.com/settings/api_keys"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email d'envoi (from)</label>
                <input
                  type="email"
                  value={form.from_email || ""}
                  onChange={(e) => setForm({ ...form, from_email: e.target.value })}
                  placeholder="noreply@votredomaine.fr"
                  className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Email de réception (to)</label>
                <input
                  type="email"
                  value={form.notification_email || ""}
                  onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
                  placeholder="contact@votreentreprise.fr"
                  className="w-full px-4 py-3 rounded-lg bg-[#131B2F] border border-white/10 outline-none"
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.send_demo_notifications !== false} onChange={(e) => setForm({ ...form, send_demo_notifications: e.target.checked })} className="w-5 h-5 rounded" />
                <span>Envoyer une notification pour les demandes de démo</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.send_contact_notifications !== false} onChange={(e) => setForm({ ...form, send_contact_notifications: e.target.checked })} className="w-5 h-5 rounded" />
                <span>Envoyer une notification pour les messages de contact</span>
              </label>
            </div>
          </>
        )}

        <div className="flex items-center gap-4 pt-4 border-t border-white/10">
          <button type="submit" className="px-6 py-3 btn-primary rounded-lg">Enregistrer</button>
          {form.provider && form.provider !== "none" && form.api_key && (
            <button type="button" onClick={testEmail} disabled={testing} className="px-6 py-3 rounded-lg border border-[#00CCC2] text-[#00CCC2] hover:bg-[#00CCC2]/10 transition-colors">
              {testing ? "Test en cours..." : "Tester l'envoi"}
            </button>
          )}
          {saved && <span className="text-green-400 flex items-center gap-2"><Check className="w-5 h-5" /> Enregistré !</span>}
        </div>

        {testResult && (
          <div className={`p-4 rounded-lg ${testResult.status === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
            {testResult.message}
          </div>
        )}
      </form>
    </div>
  );
};

export default AdminPanel;
